import React, { useContext, useEffect, useState, useRef } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { PhoneOff, Filter as FilterIcon, ArrowUp, ArrowDown } from "lucide-react";
import SharedDataContext from "../../../SharedDataContext";

import { isEmptyObject, returnArray } from "../../../utils/GenericFn";
import TableLoader from "../../Loaders/TableLoader";
import NoData from "../../NoData";
import Pagination from "../../Pagination";
import EditColumn from "../../EditColumn";
import Filter from "../../Filter";
import PrintDate from "../../Tags/PrintDate";
import Text from "../../Tags/Text";
import Image from "../../Tags/Image";
import { CallScoreBadge } from "../../ui/call-score-badge";
import { TopicBadge } from "../../ui/topic-badge";
import { Checkbox } from "../../ui/checkbox";
import { getCallsList } from "../../../apis/callLogs";
import TooltipWrapper from "../../TooltipWrapper";
import { useTableData } from "../../../hooks/useTableData";
import AccessDeniedView from "./components/AccessDeniedView";
import PageHeader from "../../PageHeader";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import NavAnchor from "../../Tags/NavAnchor";

export default function CallLogs({ isNavigating = false }) {
  const navigate = useNavigate();
  const { workspaceSlug } = useParams();
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const captureEvent = useCaptureEvent();
  const hasTrackedPageViewRef = useRef(false);

  const [callLogs, setCallLogs] = useState({});

  const [selectedRows, setSelectedRows] = useState([]);
  const [searchString, setSearchString] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  const {
    data,
    paginationInfo,
    filterMetadata,
    columnMetadata,
    apiResponse,
    isLoading,
    isFetching,
    isFetched,
    isSuccess,
    isError,
    filters,
    sort,
    pagination,
    columns,
    sortedVisibleColumns,
    getPageNumber,
    getFiltersObject,
    handleSetSort,
    getColumns,
  } = useTableData({
    queryKey: "get_calls_list",
    fetchFn: getCallsList,
    defaultHeaders: { "X-Workspace-Id": activeWorkspace?.id },
    enabled: !!activeWorkspace?.id,
    onSuccess: (data) => {
      const mappedData = mapApiResponseToTableData(data);
      console.log("mappedData", mappedData);
      setCallLogs(mappedData);
      setIsInitialLoad(false);
      setAccessDenied(false);
      console.log("Fetched!", data);
    },
    onError: (error) => {
      console.error("CallLogs API error:", error);
      if (error?.status === 403 || error?.response?.status === 403) {
        setAccessDenied(true);
        setIsInitialLoad(false);
      } else {
        setAccessDenied(false);
      }
    },
  });

  const getExternalPartyPhone = (item) => {
    if (item?.call_direction === "Outgoing") {
      return item?.to_number || "No Number";
    } else {
      return item?.from_number || "No Number";
    }
  };

  const getPrimeRoleNumber = (item) => {
    if (item?.call_direction === "Outgoing") {
      return item?.from_number || "-";
    } else {
      return item?.to_number || "-";
    }
  };

  const getCallDetailsId = (item) => {
    return item?.id || item?.provider_recording_id;
  };

  const renderTopics = (topics) => {
    const topicsArray = Array.isArray(topics) ? topics : [];
    if (topicsArray.length === 0) return <span className="text-gray-400">-</span>;

    const visibleCount = topicsArray.length > 1 ? 1 : topicsArray.length;
    const visibleTopics = topicsArray.slice(0, visibleCount);
    const hiddenTopics = topicsArray.slice(visibleCount);

    return (
      <div className="flex flex-wrap gap-1 items-center">
        {visibleTopics.map((topic, index) => (
          <TopicBadge key={index}>{topic || "-"}</TopicBadge>
        ))}
        {hiddenTopics.length > 0 && (
          <TopicBadge className="cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-800/40">
            +{hiddenTopics.length}
          </TopicBadge>
        )}
      </div>
    );
  };

  const renderTopicsTooltip = (topics) => {
    const topicsArray = Array.isArray(topics) ? topics : [];
    if (topicsArray.length === 0) return "No topics available";

    return (
      <div className="space-y-1">
        {topicsArray.map((topic, index) => (
          <div key={index} className="text-xs">
            {topic || "-"}
          </div>
        ))}
      </div>
    );
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    try {
      const date = new Date(isoString);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "-";
    }
  };

  const mapApiResponseToTableData = (apiData) => {
    if (!apiData?.data) return { data: [], columnMetadata: {}, filterMetadata: {}, pagination: {} };

    const mappedData = apiData.data.map((item) => ({
      ...item,

      contact_name: item?.user_name || "Unknown Contact",
      contact_phone: getExternalPartyPhone(item),

      name: item?.user_name || "Unknown Contact",
      duration: formatDuration(item?.connected_duration_seconds, item?.call_status),
      date_time: formatDateTime(item?.updated_at || item?.created_at),

      primerole_number: getPrimeRoleNumber(item),
      call_details_id: getCallDetailsId(item),
      topics_display: Array.isArray(item?.topics) ? item.topics : [],
    }));

    const paginationData = apiData.pagination
      ? {
          currentPage: parseInt(apiData.pagination.page) || 1,
          lastPage: apiData.pagination.last_page || 1,
          perPage: parseInt(apiData.pagination.per_page) || 12,
          total: apiData.pagination.total || 0,
        }
      : {};

    return {
      data: mappedData,
      columnMetadata: apiData.columnMetadata || {},
      filterMetadata: apiData.filterMetadata || {},
      pagination: paginationData,
    };
  };

  // Handle workspace changes
  useEffect(() => {
    setIsInitialLoad(true);
  }, [workspaceSlug]);

  useEffect(() => {
    if (activeWorkspace?.id && !hasTrackedPageViewRef.current) {
      captureEvent("SET_CallLogs_PageView", {
        workspace_id: activeWorkspace.id,
      });
      hasTrackedPageViewRef.current = true;
    }
  }, [activeWorkspace?.id, captureEvent]);

  const handleSelectAll = () => {
    setSelectedRows(
      selectedRows.length === callLogs?.data?.length ? [] : callLogs.data.map((item) => item.id)
    );
  };

  const handleSelectRow = (isChecked, id) => {
    setSelectedRows((prev) => (isChecked ? [...prev, id] : prev.filter((rowId) => rowId !== id)));
  };

  const handleRowClick = (item) => {
    const callId = getCallDetailsId(item);

    if (!callId) {
      console.error("No valid call ID found for analysis");

      return;
    }

    if (item?.call_status === "rejected" && item?.connected_duration_seconds === 0) {
      console.warn("Call may not have analysis data available");
    }

    captureEvent("SET_CallLogs_RowClick", {
      call_id: callId,
      call_direction: item?.call_direction,
    });
    navigate(`/${workspaceSlug}/call-logs/${callId}`, {
      state: {
        recordingUrl: item?.recording_url,
        callMetadata: item,
      },
    });
  };

  const getCallScoreBadgeVariant = (score) => {
    if (score >= 80) return "success";
    if (score >= 60) return "warning";
    if (score >= 40) return "default";
    return "error";
  };

  const formatDuration = (seconds, status) => {
    if (!seconds && status === "ongoing") return "Active";
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getCallStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return (
          <div className="h-9 w-9 dark:bg-green-900 flex items-center justify-center">
            <Image
              src="/images/icons/untitledui/call-logs/outgoing.svg"
              alt="Completed call"
              className="h-4 w-4"
            />
          </div>
        );
      case "Failed":
      case "missed":
        return (
          <div className="h-9 w-9  dark:bg-red-900 flex items-center justify-center">
            <Image
              src="/images/icons/untitledui/call-logs/failed.svg"
              alt="Missed call"
              className="h-4 w-4 filter-red"
            />
          </div>
        );
      case "ongoing":
      case "in-progress":
        return (
          <div className="h-9 w-9 flex items-center justify-center">
            <Image
              src="/images/icons/untitledui/call-logs/ongoing.svg"
              alt="Ongoing call"
              className="h-4 w-4"
            />
          </div>
        );
      case "brief call":
        return (
          <div className="h-9 w-9  dark:bg-yellow-900 flex items-center justify-center">
            <Image
              src="/images/icons/untitledui/call-logs/incoming.svg"
              alt="Brief call"
              className="h-4 w-4 "
            />
          </div>
        );
      default:
        return (
          <div className="h-9 w-9 rounded-full bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
            <PhoneOff className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </div>
        );
    }
  };
  const getCallTypeIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "outgoing":
        return (
          <div className="h-9 w-9  flex items-center justify-center">
            <Image
              src="/images/icons/untitledui/call-logs/outgoing.svg"
              alt="Completed call"
              className="h-5 w-5 no-invert"
            />
          </div>
        );
      case "Failed":
      case "missed":
        return (
          <div className="h-9 w-9  dark:bg-red-900 flex items-center justify-center">
            <Image
              src="/images/icons/untitledui/call-logs/failed.svg"
              alt="Missed call"
              className="h-5 w-5 filter-red no-invert"
            />
          </div>
        );
      case "ongoing":
      case "in-progress":
        return (
          <div className="h-9 w-9 flex items-center justify-center">
            <Image
              src="/images/icons/untitledui/call-logs/ongoing.svg"
              alt="Ongoing call"
              className="h-5 w-5 no-invert"
            />
          </div>
        );
      case "incoming":
        return (
          <div className="h-9 w-9 flex items-center justify-center">
            <Image
              src="/images/icons/untitledui/call-logs/incoming.svg"
              alt="Brief call"
              className="h-5 w-5 no-invert"
            />
          </div>
        );
      default:
        return (
          <div className="h-9 w-9 rounded-full bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
            <PhoneOff className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          </div>
        );
    }
  };

  const renderCellContent = (columnKey, item) => {
    switch (columnKey) {
      case "name":
        return (
          <div className="flex items-center">
            <div className="h-9 w-9 flex-shrink-0 mr-3">{getCallTypeIcon(item?.call_direction)}</div>
            <div>
              <NavAnchor
                to={`/call-logs/${getCallDetailsId(item)}`}
                className="font-medium text-gray-900 dark:text-white hover:text-brand-700 dark:hover:text-brand-400"
              >
                {"Unknown Contact"}
              </NavAnchor>
              <div className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                {getExternalPartyPhone(item)}
              </div>
            </div>
          </div>
        );
      case "user_name":
        return (
          <div className="flex items-center">
            <div>
              <div className="font-medium text-gray-900 dark:text-white">
                {item?.name || item?.user_name || "Unknown Contact"}
              </div>
            </div>
          </div>
        );

      case "call_status":
        return getCallStatusIcon(item?.call_status);

      case "primerole_number":
      case "phone_number":
        return (
          <div>
            <div className="font-medium">{item?.primerole_number || getPrimeRoleNumber(item)}</div>
          </div>
        );

      case "duration":
      case "connected_duration_seconds":
        return (
          <span className={item?.call_status === "ongoing" ? "text-blue-600 font-medium" : ""}>
            {item?.duration || formatDuration(item?.connected_duration_seconds, item?.call_status)}
          </span>
        );

      case "date_time":
      case "created_at":
        return item?.date_time || <PrintDate date={item?.updated_at || item?.created_at} showTime={true} />;

      case "topics":
        return (
          <TooltipWrapper content={renderTopicsTooltip(item?.topics_display)}>
            {renderTopics(item?.topics_display)}
          </TooltipWrapper>
        );

      case "call_score":
        return item?.call_score ? (
          <CallScoreBadge score={item.call_score} />
        ) : (
          <span className="text-gray-400">-</span>
        );

      default:
        return item?.[columnKey] || "-";
    }
  };

  if (accessDenied) {
    return (
      <div className="space-y-6">
        <AccessDeniedView />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        heading="Call Recordings"
        description="View call logs, transcripts and analyze calls with AI"
      />

      {callLogs?.data && (callLogs.data.length > 0 || !isEmptyObject(filters)) && (
        <div className="flex justify-between items-center">
          <div className="space-x-3 inline-flex items-center">
            {callLogs.filterMetadata && (
              <>
                <span className="text-sm text-gray-500">Filters:</span>
                <Filter filterMetadata={callLogs.filterMetadata} getFiltersObject={getFiltersObject} />
              </>
            )}
          </div>
          <div className="space-x-3">
            {callLogs.columnMetadata && (
              <EditColumn columnMetadata={callLogs.columnMetadata} getColumns={getColumns} />
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div>
        {callLogs?.data && callLogs.data.length > 0 && isSuccess && (
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden border border-border rounded-[--radius]">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-background">
                      <tr>
                        <th scope="col" role="checkbox" className="h-4 w-4 !pr-0">
                          <Checkbox
                            className="h-4 w-4"
                            checked={selectedRows.length === callLogs.data.length}
                            indeterminate={
                              selectedRows.length > 0 && selectedRows.length < callLogs.data.length
                            }
                            onCheckedChange={handleSelectAll}
                          />
                        </th>
                        {returnArray(sortedVisibleColumns).map((column, index) => (
                          <th
                            key={index}
                            scope="col"
                            className={`px-3 py-3.5 text-left text-xs font-medium text-text-600 ${
                              column.key === "user_name" ? "w-1/4" : ""
                            } ${column.sortable ? "cursor-pointer" : ""}`}
                            onClick={() => (column.sortable ? handleSetSort(column.key) : void 0)}
                          >
                            <span className={"inline-flex items-center"}>
                              {column.display_name}
                              {column.sortable && (
                                <>
                                  {sort === `-${column.key}` ? (
                                    <ArrowDown className="inline-block ml-1 h-3 w-3" />
                                  ) : sort === column.key ? (
                                    <ArrowUp className="inline-block ml-1 h-3 w-3" />
                                  ) : (
                                    <ArrowDown className="inline-block ml-1 h-3 w-3 opacity-50" />
                                  )}
                                </>
                              )}
                            </span>
                          </th>
                        ))}
                        <th scope="col" className="!px-3 !py-4 text-left text-sm font-medium" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border bg-background">
                      {callLogs.data &&
                        callLogs.data.length > 0 &&
                        returnArray(callLogs.data).map((item, index) => (
                          <tr
                            key={item.id}
                            className={`hover:bg-gray-50  cursor-pointer ${index % 2 !== 0 ? "" : ""}`}
                            onClick={() => handleRowClick(item)}
                          >
                            <td
                              role="checkbox"
                              className="h-4 w-4 !pr-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                className="h-4 w-4"
                                checked={selectedRows.includes(item.id)}
                                onCheckedChange={(isChecked) => handleSelectRow(isChecked, item.id)}
                              />
                            </td>
                            {returnArray(sortedVisibleColumns).map(({ key: colKey }) => (
                              <td
                                key={colKey}
                                className="whitespace-nowrap !px-3 !py-4 text-sm text-gray-900 dark:text-[#F7F7F7] "
                              >
                                {renderCellContent(colKey, item)}
                              </td>
                            ))}
                            <td className="whitespace-nowrap !px-3 !py-4"></td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {(isLoading || isInitialLoad || isNavigating) && <TableLoader />}

        {isError && !accessDenied && (
          <NoData title="Error loading call logs" description="Please try again or contact support" />
        )}

        {callLogs?.data?.length === 0 && !isLoading && !isError && (
          <NoData title="No call logs found" description="Start making calls to see them here" />
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between rounded-[--radius] py-3">
          <div className="flex-1">
            {callLogs.pagination && callLogs.pagination.total > 0 && (
              <Pagination data={callLogs.pagination} getPageNumber={getPageNumber} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
